import * as React from 'react';
import { IPergunta } from '../../interfaces/IResponderFormularioResponse';
import CardDeImagemParaUpload from '../CardDeImagemParaUpload';
import { ResponderFormularioContext } from '../../contexts/ResponderFormularioContext';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import { LanguageContext } from '../../../../../context/LanguageContext';

const { useState, useContext, useRef, useEffect } = React;

interface IProps {
	pergunta: IPergunta;
};

const CampoDeAnexoDeImagens = ({ pergunta }: IProps) => {
	const { mostrarTodosOsCollapses } = useContext(ResponderFormularioContext);
    const [isOpen, setIsOpen] = useState(true);
    const [pictureModalOpen, setPictureModalOpen] = useState(false);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

	useDidUpdateEffect(() => setIsOpen(mostrarTodosOsCollapses), [mostrarTodosOsCollapses]);

    // Detecta se é dispositivo móvel
    useEffect(() => {
        const checkIfMobile = () => {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        };
        setIsMobile(checkIfMobile());
    }, []);

    // Cleanup da câmera quando o modal é fechado
    useEffect(() => {
        if (!pictureModalOpen && stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
            setIsCameraActive(false);
        }
        
        // Cleanup quando o componente é desmontado
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [pictureModalOpen, stream]);

    const handleTakeAPicture = () => {
        setPictureModalOpen(true);
        if (isMobile) {
            // Para mobile, usar input file com capture
            setTimeout(() => {
                fileInputRef.current?.click();
            }, 100);
        } else {
            // Para desktop, iniciar câmera
            startCamera();
        }
    };

    const startCamera = async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment', // Câmera traseira
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            });
            
            setStream(mediaStream);
            setIsCameraActive(true);
            
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (error) {
            console.error('Erro ao acessar câmera:', error);
            alert('Não foi possível acessar a câmera. Verifique as permissões.');
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
            setIsCameraActive(false);
        }
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            
            if (context) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                context.drawImage(video, 0, 0);
                
                // Converte canvas para blob e simula captura
                canvas.toBlob((blob) => {
                    if (blob) {
                        alert('Foto capturada com sucesso!');
                        closeModal();
                    }
                }, 'image/jpeg', 0.8);
            }
        }
    };

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            alert('Foto capturada com sucesso!');
            closeModal();
            // Reset input
            event.target.value = '';
        }
    };

    const closeModal = () => {
        setPictureModalOpen(false);
        stopCamera();
    };

    return (
        <div className='col-12 mb-4' style={ { maxHeight: '350px' } }>
            <div className={`card ${isOpen && 'h-100'}`}  style={{ border: '1px solid var(--ws-blue-border)' }}>
                <button className='btn' onClick={handleTakeAPicture} type='button'>Tirar foto</button>
                <div className={`card-body barra_de_rolagem_estilizada ${isOpen ? '' : 'd-none'}`} style={{ overflowY: 'auto', height: '100%' }}>
                    <div className='row justify-content-around h-100'>
						{
							pergunta.imagens.filter(imagem => imagem.status !== 'Removido').length > 0
								? pergunta.imagens.filter(imagem => imagem.status !== 'Removido').map(imagem => <CardDeImagemParaUpload anexoDeImagem={imagem} idDaPergunta={pergunta.id} />)
								: <div className='text-center'>Nenhuma imagem anexada</div>
						}
					</div>
                </div>
            </div>
            {pictureModalOpen && (
                <div id="modalPictureCapture" className="modal d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Capturar Imagem</h5>
                                <button type="button" className="close" aria-label="Close" onClick={closeModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                {isMobile ? (
                                    // Interface para mobile - usa input file
                                    <div className="text-center">
                                        <div className="mb-3">
                                            <i className="fas fa-camera fa-3x text-primary mb-3"></i>
                                            <h6>Capturar foto usando a câmera do dispositivo</h6>
                                            <p className="text-muted">Toque no botão abaixo para abrir a câmera</p>
                                        </div>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            capture="environment"
                                            onChange={handleFileInput}
                                            style={{ display: 'none' }}
                                        />
                                        <button 
                                            className="btn btn-primary btn-lg"
                                            onClick={() => fileInputRef.current?.click()}
                                            type='button'
                                        >
                                            📷 Abrir Câmera
                                        </button>
                                    </div>
                                ) : (
                                    // Interface para desktop - mostra preview da câmera
                                    <div>
                                        {isCameraActive ? (
                                            <div className="camera-preview text-center">
                                                <video
                                                    ref={videoRef}
                                                    autoPlay
                                                    playsInline
                                                    muted
                                                    style={{
                                                        width: '100%',
                                                        maxHeight: '400px',
                                                        borderRadius: '8px',
                                                        backgroundColor: '#000'
                                                    }}
                                                />
                                                <div className="mt-3">
                                                    <button 
                                                        className="btn btn-success btn-lg me-3"
                                                        onClick={capturePhoto}
                                                        type='button'
                                                    >
                                                        📷 Capturar Foto
                                                    </button>
                                                    <button 
                                                        className="btn btn-secondary"
                                                        onClick={stopCamera}
                                                        type='button'
                                                    >
                                                        ⏹️ Parar Câmera
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center">
                                                <div className="mb-3">
                                                    <i className="fas fa-video fa-3x text-primary mb-3"></i>
                                                    <h6>Câmera não iniciada</h6>
                                                    <p className="text-muted">Clique no botão abaixo para iniciar a câmera</p>
                                                </div>
                                                <button 
                                                    className="btn btn-primary btn-lg"
                                                    onClick={startCamera}
                                                    type='button'
                                                >
                                                    🎥 Iniciar Câmera
                                                </button>
                                            </div>
                                        )}
                                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampoDeAnexoDeImagens;
